import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-sm">
        <Logo size="lg" className="mb-8" />
        {sent ? (
          <div className="rounded-2xl border border-border bg-card p-8" data-testid="forgot-sent">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><MailCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="text-2xl font-bold tracking-tight">Check your email</h1>
            <p className="text-muted-foreground mt-2 text-sm">If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent password reset instructions. The link expires in 60 minutes.</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline" data-testid="forgot-back-login"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight">Forgot password?</h1>
            <p className="text-muted-foreground mt-2">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="username" type="email" autoComplete="username" required value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11" data-testid="forgot-email-input" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg" data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-login"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
